import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as ApplicationsService from '../services/ApplicationsService';
import { Application } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const applicationsKeys = {
    all: ['applications'] as const,
    lists: () => [...applicationsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...applicationsKeys.lists(), { filters }] as const,
    details: () => [...applicationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...applicationsKeys.details(), id] as const,
};

const PAGE_SIZE = 50;

// FNP-913: TOKEN PAGINATION.
//
// This was `useQuery` calling a service that returned `.items` and threw
// `nextToken` away — so the list was never paginated at all. It read as
// compliant (query hook ✓, structured keys ✓, invalidation ✓) while fetching
// one unbounded page, which is exactly the failure mode §9 of the module
// checklist now calls out explicitly.
//
// getNextPageParam MUST return `undefined` (not null, not the same token) when
// there are no more pages, or react-query refetches forever.
export const useApplicationsInfiniteQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        queryKey: applicationsKeys.list(filters),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            ApplicationsService.fetchRecords(filters, PAGE_SIZE, pageParam),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        initialPageParam: null as string | null,
    });
};

export const useApplicationQuery = (id: string) => {
    return useQuery({
        queryKey: applicationsKeys.detail(id),
        queryFn: () => ApplicationsService.fetch(id),
        enabled: !!id,
    });
};

// FNP-1028 sibling audit, 2026-09-19. Checked by opening each, not assumed.
//
//   create      onSuccess + invalidate(lists)          — correct as is
//   update      onSuccess + invalidate(lists, detail)  — correct as is
//   delete      MOVED TO onSettled                     — see its own note
//
// ⚠️ ONLY `delete` NEEDS `onSettled`, and the reason is specific rather than
// stylistic. `deleteApplication` returns `Application`, whose `id` is non-null
// — and after a delete there is no id to return, which is why FNP-989 makes it
// reject on an operation that SUCCEEDED. It is the one mutation here whose
// rejection does not imply its write failed.
//
// Every other mutation returns an `Application` that still exists afterwards.
// A rejection from those means the write genuinely did not happen, so the
// cached list is still accurate and an `onSettled` refetch would be a refetch
// for nothing.
export const useCreateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationsService.add,
        onSuccess: () => {
            notifySuccess('Application created');
            queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: Application }) =>
            ApplicationsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Application updated');
            queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: applicationsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

// FNP-1028: THE LIST MUST REFRESH ON BOTH PATHS, NOT ONLY ON SUCCESS.
//
// The owner deleted `river-joe` in test, saw an error, and saw the row still
// sitting in the list. The delete had worked — the record was gone from the
// table. Every visible signal said otherwise, and the obvious next move is to
// click Delete again, on a list that has since shifted.
//
// ⚠️ INVALIDATE ON `onSettled`, NOT `onSuccess`. FNP-989 makes
// `deleteApplication` REJECT on a delete that actually happened:
//
//     Cannot return null for non-nullable type: 'ID' within parent
//     'Application' (/deleteApplication/id)
//
// `onSuccess` does not run on a rejection, so an onSuccess-only invalidation
// leaves the list stale in EXACTLY the case that produced the report. This is
// not defensive coding against a hypothetical — it is the observed path.
//
// The notification stays on `onSuccess`: announcing "Application deleted" from
// `onSettled` would claim a success on a genuine failure, which is the
// opposite mistake. Refreshing on a failure costs one refetch; claiming a
// deletion that did not happen costs the operator's trust in the screen.
export const useDeleteApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Application deleted');
        },
        onError: notifyError,
        // Runs after success AND after error. The list is re-read either way,
        // so what is on screen is what the API actually holds.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: applicationsKeys.all });
        },
    });
};

// Global filter state (shared across components)
//
// FNP-1546: NO DEFAULT FILTER. This used to start at
// `{applicationStatus: 'ACTIVE'}`, a filter the operator never chose.
//
// ⚠️ AN ABSENT STATUS DOES NOT MATCH 'ACTIVE'. A freshly created record has no
// `applicationStatus` attribute at all, so the default hid every application
// from the moment it was created — the create succeeded, the toast said so,
// and the row was not on the list. Creating it again then failed the
// uniqueness check, which is an unintelligible error about a record the screen
// says does not exist. It also put two different numbers for one question on
// one screen: the dashboard card counted unfiltered (11), the list filtered (9).
//
// The operator opts IN to a status filter; the control is right there and
// `handleClearFilters` has always been able to reach this empty state.
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useApplicationsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
